import React, { useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import { Bell, Filter, CheckCircle, Trash2, ArrowRight } from 'lucide-react';

const AllNotifications = () => {
  const { state, dispatch } = useData();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all'); // all, unread, priority

  const filteredAlerts = state.alerts.filter(alert => {
    if (filter === 'unread') return !alert.isRead;
    if (filter === 'high') return alert.priority === 'high';
    if (filter === 'medium') return alert.priority === 'medium';
    if (filter === 'low') return alert.priority === 'low';
    return true;
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const unreadCount = state.alerts.filter(a => !a.isRead).length;

  const handleMarkAsRead = (alertId) => {
    dispatch({
      type: 'UPDATE_ALERT',
      payload: { id: alertId, updates: { isRead: true } },
    });
  };

  const handleMarkAllAsRead = () => {
    state.alerts.filter(a => !a.isRead).forEach(alert => {
      dispatch({
        type: 'UPDATE_ALERT',
        payload: { id: alert.id, updates: { isRead: true } },
      });
    });
  };

  const priorityColors = {
    high: 'bg-red-50 border-l-4 border-red-500',
    medium: 'bg-orange-50 border-l-4 border-orange-500',
    low: 'bg-green-50 border-l-4 border-green-500',
  };

  const priorityBadgeColors = {
    high: 'bg-red-500 text-white',
    medium: 'bg-orange-500 text-white',
    low: 'bg-green-500 text-white',
  };

  const priorityText = {
    high: 'Alta',
    medium: 'Media',
    low: 'Baja',
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">
          Notificaciones
        </h1>
        <p className="text-lg text-slate-500">
          Gestiona tus recordatorios y alertas
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Bell className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-2xl font-bold text-slate-900">{state.alerts.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Sin leer</p>
              <p className="text-2xl font-bold text-blue-600">{unreadCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Bell className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Alta prioridad</p>
              <p className="text-2xl font-bold text-red-600">
                {state.alerts.filter(a => a.priority === 'high').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Leídas</p>
              <p className="text-2xl font-bold text-green-600">
                {state.alerts.filter(a => a.isRead).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Filtrar:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 text-sm"
            >
              <option value="all">Todas</option>
              <option value="unread">Sin leer</option>
              <option value="high">Prioridad Alta</option>
              <option value="medium">Prioridad Media</option>
              <option value="low">Prioridad Baja</option>
            </select>
          </div>

          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              <CheckCircle className="w-4 h-4" />
              Marcar todas como leídas
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredAlerts.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              No hay notificaciones
            </h3>
            <p className="text-slate-500">
              {filter !== 'all' 
                ? 'Prueba cambiando el filtro'
                : 'Las notificaciones aparecerán aquí'}
            </p>
          </div>
        ) : (
          filteredAlerts.map((alert) => {
            const lead = state.leads.find(l => l.id === alert.leadId);
            const isOverdue = new Date(alert.date) < new Date();

            return (
              <div
                key={alert.id}
                className={`bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all cursor-pointer ${
                  !alert.isRead ? priorityColors[alert.priority] : ''
                }`}
                onClick={() => navigate(`/lead/${alert.leadId}`)}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${
                      !alert.isRead ? 'bg-indigo-600' : 'bg-slate-300'
                    }`}
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-slate-900">
                            {alert.title}
                          </h3>
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${priorityBadgeColors[alert.priority] || 'bg-slate-500 text-white'}`}>
                            {priorityText[alert.priority] || 'Media'}
                          </span>
                          {isOverdue && !alert.isRead && (
                            <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full font-semibold">
                              Vencido
                            </span>
                          )}
                        </div>

                        {lead && (
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-indigo-600 font-semibold text-xs">
                                {lead.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{lead.name}</p>
                              <p className="text-xs text-slate-500">{lead.company || 'Sin empresa'}</p>
                            </div>
                          </div>
                        )}

                        {alert.description && (
                          <p className="text-sm text-slate-600 mb-2">
                            {alert.description}
                          </p>
                        )}

                        <p className="text-xs text-slate-500">
                          📅 {new Date(alert.date).toLocaleString('es-ES', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {!alert.isRead && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(alert.id);
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors font-medium"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Marcar leída
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/lead/${alert.leadId}`);
                          }}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          <ArrowRight className="w-5 h-5 text-slate-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AllNotifications;
